<template>
    <div
        ref="jumpLinksHorizontal"
        class="jump-links-horizontal-container"
    >
        <nav
            :class="[
                'jump-links-horizontal',
                `jump-links-horizontal--alignment-${alignment}`,
                {
                    'jump-links-horizontal--full-width': fullWidth,
                }
            ]"
        >
            <div class="jump-links-horizontal__inner">
                <span
                    class="jump-links-horizontal__list-bar"
                    :style="{ width: `${activeItemWidth}px`, left: `${activeItemPosition}px` }"
                ></span>

                <ul class="jump-links-horizontal__list">
                    <li
                        v-for="item in items"
                        :key="item.label || item.name"
                        class="jump-links-horizontal__list-item"
                    >
                        <a
                            :href="`#${item.id}`"
                            class="jump-links-horizontal__list-item-link u-paragraph"
                            :class="{ 'jump-links-horizontal__list-item--active u-bold': activeItemId === item.id }"
                            @click.prevent="handleClick(item)"
                        >
                            {{ item.name }}
                        </a>
                    </li>
                </ul>
            </div>
        </nav>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { scrollToSelector } from '@utils/scroll-to-selector';

const props = defineProps({
    items: {
        type: [Object, Array],
        required: true,
    },
    alignment: {
        type: String,
        default: 'center',
    },
    fullWidth: {
        type: Boolean,
        default: false,
    },
});

const activeItem = ref(null);
const activeItemWidth = ref(0);
const activeItemPosition = ref(0);
const activeItemId = ref(props.items[0].id || '');
const jumpLinksHorizontal = ref(null);
const isManuallyScrolling = ref(false);

const triggerScroll = (selector, offset) => {
    scrollToSelector(selector, offset);
};

const handleClick = item => {
    isManuallyScrolling.value = true;
    activeItemId.value = item.id;
    triggerScroll(`#${item.id}`, -70);
    handleActiveItem();

    setTimeout(() => {
        isManuallyScrolling.value = false;
    }, 500);
};

const handleActiveItem = () => {
    setTimeout(() => {
        activeItem.value = jumpLinksHorizontal.value.querySelector('.jump-links-horizontal__list-item--active');

        if (activeItem.value) {
            activeItemWidth.value = activeItem.value.offsetWidth;
            activeItemPosition.value = activeItem.value.offsetLeft;

            activeItem.value.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center',
            });
        }
    }, 0);
};

const handleScroll = () => {
    if (isManuallyScrolling.value) return;

    const isAtBottom
        = window.innerHeight + window.scrollY >= document.body.scrollHeight - 5;

    if (isAtBottom) {
        const last = props.items[props.items.length - 1];
        if (last.id !== activeItemId.value) {
            activeItemId.value = last.id;
            handleActiveItem();
        }
        return;
    }

    const active = props.items.reduce((acc, item) => {
        const el = document.getElementById(item.id);
        if (!el) return acc;

        const rect = el.getBoundingClientRect();

        if (rect.top < 100) {
            return item;
        }

        return acc;
    }, props.items[0]);

    if (active.id !== activeItemId.value) {
        activeItemId.value = active.id;
        handleActiveItem();
    }
};

onMounted(() => {
    activeItemId.value = props.items[0].id;

    setTimeout(() => {
        handleActiveItem();
        window.addEventListener('scroll', handleScroll, { passive: true });
    }, 0);
});

onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll);
});
</script>

<style lang="scss">
@use 'watson-organism-jump-links-horizontal';
</style>
